import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
  faPhoneSlash,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import httpRequest from "src/helpers/httpRequest";
import ConfirmationModal from "src/Components/GlobalComponents/ConfirmationModal";
import SplashScreen from "src/Components/SplashScreen";
import { useSelector } from "react-redux";
import { RootState } from "src/Store/store";
import { socket } from "src/Sockets/socket";

// A fallback image for either local or remote side
import defaultProfile from "src/assets/images/default_profile_image.png";
import { capitalize } from "@mui/material";

interface IMeetingDetails {
  userDetails: string;
  userImage: string;
  appointmentId: string;
  roomId: string;
}

// WebRTC Track Interface
interface IWebRTCTrack {
  attach: (element: HTMLVideoElement | HTMLAudioElement) => void;
  detach: (element?: HTMLVideoElement | HTMLAudioElement) => void;
  stop: () => void;
  disable: () => void;
  enable: () => void;
  mediaStreamTrack: MediaStreamTrack;
  kind: string;
  sid?: string;
}

const OnlineConsultations: React.FC = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);

  // ---------------------------
  // WEBRTC STATES
  // ---------------------------
  const [peerConnection, setPeerConnection] =
    useState<RTCPeerConnection | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  // Local user states
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMuted, setIsMuted] = useState(false);

  // Remote user states
  const [isRemoteCameraOn, setIsRemoteCameraOn] = useState(true);
  const [isRemoteConnected, setIsRemoteConnected] = useState(false);

  // Local track references
  const [localVideoTrack, setLocalVideoTrack] = useState<IWebRTCTrack | null>(
    null
  );
  const [localAudioTrack, setLocalAudioTrack] = useState<IWebRTCTrack | null>(
    null
  );
  const [remoteVideoTrack, setRemoteVideoTrack] = useState<IWebRTCTrack | null>(
    null
  );
  const [remoteAudioTrack, setRemoteAudioTrack] = useState<IWebRTCTrack | null>(
    null
  );

  // Refs to video elements
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);

  // Store pending ICE candidates
  const pendingIceCandidatesRef = useRef<RTCIceCandidate[]>([]);

  // ---------------------------
  // ROUTER
  // ---------------------------
  const navigate = useNavigate();
  const location = useLocation();
  const { roomId = "", appoinmentId = "" } = useParams();

  // ---------------------------
  // UI STATES
  // ---------------------------
  const [confirmModal, setConfirmModal] = useState(false);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [meetingDetails, setMeetingDetails] = useState<IMeetingDetails | null>(
    null
  );
  const [mediaDevicesAvailable, setMediaDevicesAvailable] =
    useState<boolean>(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // SETUP MEDIA TRACKS
  // -------------------------------------------------------------------------
  const setupMediaTracks = async () => {
    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.error("❌ MediaDevices API not supported");
        setMediaDevicesAvailable(false);
        throw new Error("MediaDevices API is not supported in this browser.");
      }

      // Request permissions first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        setLocalStream(stream);
        setMediaDevicesAvailable(true);

        // Create local video track object
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const attachedVideoElements: HTMLVideoElement[] = [];
          const webRTCVideoTrack: IWebRTCTrack = {
            attach: (element: HTMLVideoElement | HTMLAudioElement) => {
              if (element) {
                element.srcObject = stream;
                element.play().catch(console.error);
                attachedVideoElements.push(element as HTMLVideoElement);
              }
            },
            detach: (element?: HTMLVideoElement | HTMLAudioElement) => {
              if (element) {
                const index = attachedVideoElements.indexOf(
                  element as HTMLVideoElement
                );
                if (index > -1) {
                  attachedVideoElements.splice(index, 1);
                }
                if (element.srcObject) {
                  element.srcObject = null;
                }
              } else {
                attachedVideoElements.forEach((el: HTMLVideoElement) => {
                  if (el.srcObject) {
                    el.srcObject = null;
                  }
                });
                attachedVideoElements.length = 0;
              }
            },
            stop: () => {
              if (videoTrack) {
                videoTrack.stop();
              }
            },
            disable: () => {
              if (videoTrack) {
                videoTrack.enabled = false;
              }
            },
            enable: () => {
              if (videoTrack) {
                videoTrack.enabled = true;
              }
            },
            mediaStreamTrack: videoTrack,
            kind: "video",
          };
          setLocalVideoTrack(webRTCVideoTrack);
        }

        // Create local audio track object
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          const attachedAudioElements: HTMLAudioElement[] = [];
          const webRTCAudioTrack: IWebRTCTrack = {
            attach: (element: HTMLAudioElement) => {
              if (element) {
                element.srcObject = stream;
                element.autoplay = true;
                element.style.display = "none";
                attachedAudioElements.push(element);
              }
            },
            detach: (element?: HTMLAudioElement) => {
              if (element) {
                const index = attachedAudioElements.indexOf(element);
                if (index > -1) {
                  attachedAudioElements.splice(index, 1);
                }
                if (element.srcObject) {
                  element.srcObject = null;
                }
              } else {
                attachedAudioElements.forEach((el: HTMLAudioElement) => {
                  if (el.srcObject) {
                    el.srcObject = null;
                  }
                });
                attachedAudioElements.length = 0;
              }
            },
            stop: () => {
              if (audioTrack) {
                audioTrack.stop();
              }
            },
            disable: () => {
              if (audioTrack) {
                audioTrack.enabled = false;
              }
            },
            enable: () => {
              if (audioTrack) {
                audioTrack.enabled = true;
              }
            },
            mediaStreamTrack: audioTrack,
            kind: "audio",
          };
          setLocalAudioTrack(webRTCAudioTrack);
        }

        return stream;
      } catch (permissionError) {
        console.error("Permission denied for media devices:", permissionError);
        setMediaDevicesAvailable(false);
        throw new Error(
          "Camera and microphone permissions are required for video calls."
        );
      }
    } catch (error) {
      console.error("Error accessing media devices:", error);
      setMediaDevicesAvailable(false);
      toast(
        error instanceof Error
          ? error.message
          : "Failed to access media devices. Please check your camera and microphone.",
        { type: "error" }
      );
      throw error;
    }
  };

  // -------------------------------------------------------------------------
  // WEBRTC SIGNALING FUNCTIONS
  // -------------------------------------------------------------------------
  const createOffer = async (pc: RTCPeerConnection, roomId: string) => {
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      if (socket) {
        socket.emit("signal", {
          roomId: roomId,
          signalData: offer,
          userId: userDetails?._id,
        });
      }
    } catch (error) {
      console.error("❌ Error creating offer:", error);
    }
  };

  const handleOffer = async (
    pc: RTCPeerConnection,
    offer: RTCSessionDescriptionInit,
    roomId: string
  ) => {
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      if (socket) {
        socket.emit("signal", {
          roomId: roomId,
          signalData: answer,
          userId: userDetails?._id,
        });
      }
    } catch (error) {
      console.error("❌ Error handling offer:", error);
    }
  };

  // -------------------------------------------------------------------------
  // SETUP WEBRTC CONNECTION
  // -------------------------------------------------------------------------
  const setupWebRTCConnection = async () => {
    try {
      if (!userDetails?._id) {
        console.error("❌ User details not available");
        throw new Error("User information not available. Please log in again.");
      }

      const stream = await setupMediaTracks();

      // Create peer connection
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
        ],
      });

      setPeerConnection(pc);

      // Add local stream to peer connection
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && socket) {
          socket.emit("iceCandidate", {
            roomId: roomId,
            candidate: event.candidate,
          });
        }
      };

      // Handle remote stream
      pc.ontrack = (event) => {
        const remoteStream = event.streams[0];
        if (remoteStream) {
          setRemoteStream(remoteStream);
          setIsRemoteConnected(true);

          // Create remote video track object
          const remoteVideoTracks = remoteStream.getVideoTracks();
          if (remoteVideoTracks.length > 0) {
            const videoTrack = remoteVideoTracks[0];

            // Set initial camera state based on track enabled status
            setIsRemoteCameraOn(videoTrack.enabled);
            console.log("Remote video aarahi hai:", videoTrack.enabled);

            // Add event listener for track enabled/disabled changes
            videoTrack.addEventListener("ended", () => {
              setIsRemoteCameraOn(false);
              console.log("Remote video band ho gayi");
            });

            const webRTCRemoteVideoTrack: IWebRTCTrack = {
              attach: (element: HTMLVideoElement | HTMLAudioElement) => {
                if (element) {
                  element.srcObject = remoteStream;
                  element.play().catch(console.error);
                }
              },
              stop: () => {
                if (videoTrack) {
                  videoTrack.stop();
                }
              },
              detach: () => {
                // Clean up if needed
              },
              disable: () => {
                videoTrack.enabled = false;
                setIsRemoteCameraOn(false);
              },
              enable: () => {
                videoTrack.enabled = true;
                setIsRemoteCameraOn(true);
              },
              mediaStreamTrack: videoTrack,
              kind: "video",
              sid: videoTrack.id,
            };
            setRemoteVideoTrack(webRTCRemoteVideoTrack);
          } else {
            // No video tracks available, camera is off
            setIsRemoteCameraOn(false);
            console.log("Remote video nahi aarahi");
          }

          // Create remote audio track object
          const remoteAudioTracks = remoteStream.getAudioTracks();
          if (remoteAudioTracks.length > 0) {
            const audioTrack = remoteAudioTracks[0];
            const webRTCRemoteAudioTrack: IWebRTCTrack = {
              attach: (element: HTMLAudioElement) => {
                if (element) {
                  element.srcObject = remoteStream;
                  element.autoplay = true;
                  element.style.display = "none";
                }
              },
              stop: () => {
                if (audioTrack) {
                  audioTrack.stop();
                }
              },
              detach: () => {
                return [];
              },
              disable: () => {
                audioTrack.enabled = false;
              },
              enable: () => {
                audioTrack.enabled = true;
              },
              mediaStreamTrack: audioTrack,
              kind: "audio",
              sid: audioTrack.id,
            };
            setRemoteAudioTrack(webRTCRemoteAudioTrack);

            // Attach remote audio track
            webRTCRemoteAudioTrack.attach(document.createElement("audio"));
          }

          // Handle track removal
          remoteStream.addEventListener("removetrack", (event) => {
            console.log("Track removed from remote stream:", event.track.kind);
            if (event.track.kind === "video") {
              setIsRemoteCameraOn(false);
            }
          });
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed") {
          console.error("Connection failed due to network issues.");
          toast("Connection failed. Please check your network.", {
            type: "error",
          });
          retryVideoCall();
        }
      };

      // Join socket.io room for signaling
      if (socket) {
        // Join room as per backend
        socket.emit("joinRoom", roomId);

        // Listen for WebRTC signaling events
        socket.on("userJoined", (userId: string) => {
          createOffer(pc, roomId);
        });

        // Handle signal events (offers and answers)
        socket.on(
          "signal",
          async (data: {
            signal: RTCSessionDescriptionInit;
            userId: string;
          }) => {
            try {
              if (data.signal.type === "offer") {
                await handleOffer(pc, data.signal, roomId);
              } else if (data.signal.type === "answer") {
                await pc.setRemoteDescription(
                  new RTCSessionDescription(data.signal)
                );
              }
            } catch (error) {
              console.error("❌ Error handling signal:", error);
            }
          }
        );

        // Handle ICE candidates
        socket.on(
          "iceCandidate",
          async (data: { candidate: RTCIceCandidateInit }) => {
            try {
              if (pc.remoteDescription && pc.remoteDescription.type) {
                await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
              } else {
                pendingIceCandidatesRef.current.push(
                  new RTCIceCandidate(data.candidate)
                );
              }
            } catch (error) {
              console.error("❌ Error adding ICE candidate:", error);
            }
          }
        );

        // Handle user leaving
        socket.on("userLeft", (data: { userId: string; roomId: string }) => {
          setIsRemoteConnected(false);
          setIsRemoteCameraOn(false);
          toast(`Participant has left the call.`, { type: "warning" });
        });

        // Handle call ended
        socket.on(
          "callEnded",
          (data: { roomId: string; endedBy: string; duration?: number }) => {
            toast(
              `Call ended${
                data.duration
                  ? ` (Duration: ${Math.round(data.duration)}s)`
                  : ""
              }`,
              { type: "info" }
            );
            handleEndCall();
          }
        );

        // Handle call rejected
        socket.on(
          "callRejected",
          (data: { roomId: string; rejectedBy: string; reason?: string }) => {
            toast(`Call was rejected: ${data.reason || "Unknown reason"}`, {
              type: "warning",
            });
            handleEndCall();
          }
        );

        // Handle call timeout
        socket.on("callTimeout", (data: { roomId: string }) => {
          toast("Call timed out", { type: "warning" });
          handleEndCall();
        });

        // Handle camera toggle from remote user
        socket.on("cameraToggle", (data: { enabled: boolean }) => {
          setIsRemoteCameraOn(data.enabled);
          console.log(`Remote camera toggled: ${data.enabled ? "on" : "off"}`);
        });
      }

      setConnectionError(null);
    } catch (error) {
      console.error("Error setting up WebRTC connection:", error);

      let errorMessage =
        "Failed to join video call. Please check your connection.";

      if (error instanceof Error) {
        const errorStr = error.message.toLowerCase();

        if (errorStr.includes("permission") || errorStr.includes("denied")) {
          errorMessage =
            "Camera or microphone access denied. Please allow permissions and try again.";
        } else if (
          errorStr.includes("network") ||
          errorStr.includes("connection")
        ) {
          errorMessage =
            "Network connection failed. Please check your internet connection.";
        } else {
          errorMessage = error.message;
        }
      }

      setConnectionError(errorMessage);
      toast(errorMessage, { type: "error" });
    }
  };

  // -------------------------------------------------------------------------
  // RETRY
  // -------------------------------------------------------------------------
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const retryVideoCall = async () => {
    if (retryCount < maxRetries) {
      setRetryCount((prev) => prev + 1);
      setTimeout(() => {
        setupWebRTCConnection();
      }, 5000);
    } else {
      console.error("Max retry attempts reached");
      toast(
        "Unable to connect to video call after multiple attempts. Please refresh the page and try again.",
        {
          type: "error",
        }
      );
      setRetryCount(0);
    }
  };

  const handleManualRetry = async () => {
    setConnectionError(null);
    setRetryCount(0);
    await setupWebRTCConnection();
  };

  // -------------------------------------------------------------------------
  // FETCH MEETING DETAILS, START CALL
  // -------------------------------------------------------------------------
  const fetchMeetingDetails = async () => {
    setIsLoader(true);
    try {
      const { res, err } = await httpRequest<IMeetingDetails[]>({
        path: `/appoiment/meeting-details/${appoinmentId}?roomId=${roomId}`,
      });

      if (res && res.length > 0) {
        setMeetingDetails(res[0]);
        await setupWebRTCConnection();
      } else {
        console.error("❌ Invalid meeting details response");
        throw new Error("Invalid meeting details response");
      }
    } catch (error) {
      console.error("Error fetching meeting details:", error);
      toast("Unable to fetch meeting details. Please try again.", {
        type: "error",
      });
      handleEndCall();
    } finally {
      setIsLoader(false);
    }
  };

  // -------------------------------------------------------------------------
  // ATTACH VIDEO TRACKS
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (localStream && localVideoRef.current) {
      const videoElement = localVideoRef.current;

      // Only set srcObject if it's different to avoid unnecessary reloads
      if (videoElement.srcObject !== localStream) {
        videoElement.srcObject = localStream;
      }

      // Use a more robust approach to handle autoplay
      const playVideo = async () => {
        try {
          // Check if video is already playing
          if (!videoElement.paused) {
            return;
          }

          await videoElement.play();
        } catch (error) {
          console.error("❌ Error playing video:", error);

          // Handle specific autoplay policy errors
          if (error instanceof Error) {
            if (error.name === "NotAllowedError") {
              toast(
                "Please allow camera access and click anywhere to start video",
                { type: "info" }
              );
            } else if (error.name === "AbortError") {
            } else {
              toast("Video play failed. Please check permissions.", {
                type: "error",
              });
            }
          }
        }
      };

      // Small delay to ensure video element is ready
      setTimeout(playVideo, 100);
    }
  }, [localStream]);

  // Updated useEffect to handle remote video stream and prevent black screen
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = isRemoteCameraOn ? remoteStream : null;
      if (isRemoteCameraOn) {
        remoteVideoRef.current.play().catch((error) => {
          console.error("Error playing remote video:", error);
        });
      }
    } else if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null; // Clear to prevent stale black frames
    }
  }, [remoteStream, isRemoteCameraOn]);

  // -------------------------------------------------------------------------
  // INIT + CLEANUP
  // -------------------------------------------------------------------------
  useEffect(() => {
    fetchMeetingDetails();

    // Cleanup function
    return () => {
      // Remove socket event listeners
      if (socket) {
        socket.off("userJoined");
        socket.off("signal");
        socket.off("iceCandidate");
        socket.off("userLeft");
        socket.off("callEnded");
        socket.off("callRejected");
        socket.off("callTimeout");
        socket.off("cameraToggle"); // Clean up cameraToggle listener
      }
      handleEndCall();
    };
    // eslint-disable-next-line
  }, []);

  // -------------------------------------------------------------------------
  // LOCAL CAMERA TOGGLE
  // -------------------------------------------------------------------------
  const handleCameraToggle = () => {
    if (localVideoTrack) {
      const newEnabled = !isCameraOn;
      if (isCameraOn) {
        localVideoTrack.disable();
      } else {
        localVideoTrack.enable();
      }
      setIsCameraOn(newEnabled);

      // Emit camera toggle event to notify remote peer
      if (socket && roomId) {
        socket.emit("cameraToggle", {
          roomId,
          enabled: newEnabled,
        });
      }
    }
  };

  // -------------------------------------------------------------------------
  // LOCAL MUTE TOGGLE
  // -------------------------------------------------------------------------
  const handleMuteToggle = () => {
    if (localAudioTrack) {
      if (isMuted) {
        localAudioTrack.enable();
      } else {
        localAudioTrack.disable();
      }
      setIsMuted(!isMuted);
    }
  };

  // -------------------------------------------------------------------------
  // END CALL
  // -------------------------------------------------------------------------
  const handleEndCall = () => {
    // Stop all tracks
    if (localVideoTrack) {
      localVideoTrack.stop();
    }
    if (localAudioTrack) {
      localAudioTrack.stop();
    }
    if (remoteVideoTrack) {
      remoteVideoTrack.stop();
    }
    if (remoteAudioTrack) {
      remoteAudioTrack.stop();
    }

    // Stop all streams
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
    }
    if (remoteStream) {
      remoteStream.getTracks().forEach((track) => track.stop());
    }

    // Close peer connection
    if (peerConnection) {
      peerConnection.close();
    }

    // End call and leave socket room as per backend
    if (socket) {
      socket.emit("endCall", {
        roomId: roomId,
        appointmentId: appoinmentId,
      });
      socket.emit("leaveRoom", { roomId: roomId });
    }

    window.location.href =
      userDetails?.userRole === "customer" ? "/" : "/dashboard";
  };

  // -------------------------------------------------------------------------
  // CALL MANAGEMENT FUNCTIONS
  // -------------------------------------------------------------------------
  const acceptCall = () => {
    if (socket && roomId) {
      socket.emit("acceptCall", { roomId: roomId });
    }
  };

  const rejectCall = (reason?: string) => {
    if (socket && roomId) {
      socket.emit("rejectCall", {
        roomId: roomId,
        reason: reason || "Call rejected by user",
      });
    }
  };

  const startCall = () => {
    if (socket) {
      socket.emit("startCall");
    }
  };

  // -------------------------------------------------------------------------
  // COPY LINK
  // -------------------------------------------------------------------------
  const copyToClipBoard = async () => {
    try {
      const fullUrl = `${window.location.origin}${location.pathname}`;
      await navigator.clipboard.writeText(fullUrl);
      toast("Copied to clipboard.", { type: "success" });
    } catch (err) {
      toast("Something went wrong.", { type: "error" });
    }
  };

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------
  return (
    <>
      {isLoader && <SplashScreen />}

      <ConfirmationModal
        title="Are you sure you want to leave?"
        show={confirmModal}
        onClose={() => setConfirmModal(false)}
        leftBtnText="No"
        rightBtnText="Yes"
        loader={false}
        onConfirm={handleEndCall}
      />

      <div className="video-call-page">
        <div className="video-call-page-header">
          <p className="text-white">Online Consultation</p>
        </div>

        {/* Error Display */}
        {connectionError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mx-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="font-medium">Connection Error</p>
                <p className="text-sm">{connectionError}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleManualRetry}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                >
                  Retry
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}

        {/* -------------------- */}
        {/* MAIN VIDEO SECTION */}
        {/* -------------------- */}
        <div className="flex justify-evenly gap-4">
          {/* ----------------------------- */}
          {/* LOCAL USER VIDEO/IMAGE */}
          {/* ----------------------------- */}
          <div
            className="video-call-page-video relative rounded-lg md:w-[50%] md:h-[60vh] h-[35vh] w-[90%] bg-black"
            style={{ border: "2px solid #333" }}
          >
            {/* Our always-attached <video> */}
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              controls={false}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: isCameraOn ? "block" : "none",
                backgroundColor: "#000",
                borderRadius: "8px",
                zIndex: 1,
                transform: "scaleX(-1)", // Mirror the video
              }}
            />

            {/* Fallback image for local user if camera off */}
            {!isCameraOn && (
              <div className="w-full h-full flex justify-center items-center gap-2 flex-col">
                <img
                  src={`${
                    userDetails?.userRole !== "customer"
                      ? userDetails?.coverPhoto
                      : userDetails?.profileImageUrl
                  }?t=${Date.now()}`}
                  alt="local fallback"
                  className="lg:w-[7vw] lg:h-[7vw] w-[70px] h-[70px] object-cover rounded-full border-2 border-white p-1"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement; // Assert the type
                    target.src = defaultProfile;
                  }}
                />
                <h2 className="text-white font-size-18px font-Poppins-Medium">
                  {`${capitalize(userDetails?.firstName || "")} ${capitalize(userDetails?.lastName || "")}`}
                </h2>
              </div>
            )}
            <span
              className={`text-white font-size-18px bg-red-700 z-10 px-3 absolute top-0`}
            >
              {`${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`}
            </span>
          </div>

          {/* ----------------------------- */}
          {/* REMOTE USER VIDEO/IMAGE */}
          {/* ----------------------------- */}
          <div
            className="video-call-page-video relative rounded-lg md:w-[50%] md:h-[60vh] h-[35vh] w-[90%]"
            style={{ border: "2px solid #333" }}
          >
            {/* If remote user is connected, show/hide video based on isRemoteCameraOn */}
            {isRemoteConnected ? (
              <>
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full bg-black"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: isRemoteCameraOn ? "block" : "none",
                    backgroundColor: "#000",
                    borderRadius: "8px",
                  }}
                />

                {/* Fallback image if remote camera is off */}
                {!isRemoteCameraOn && (
                  <div className="w-full h-full flex justify-center items-center gap-2 flex-col">
                    <img
                      src={
                        meetingDetails?.userImage
                          ? `${meetingDetails?.userImage}?t=${Date.now()}`
                          : defaultProfile
                      }
                      alt="remote fallback"
                      className="lg:w-[7vw] lg:h-[7vw] w-[70px] h-[70px] object-cover rounded-full border-2 border-white p-1"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement; // Assert the type
                        target.src = defaultProfile;
                      }}
                    />
                    <h2 className="text-white font-size-18px font-Poppins-Medium">
                      {`${capitalize(meetingDetails?.userDetails || "")}`}
                    </h2>
                  </div>
                )}

                <span
                  className={`text-white font-size-18px bg-red-700 z-10 px-3 absolute top-0`}
                >
                  {meetingDetails?.userDetails || ""}
                </span>
              </>
            ) : (
              // If remote user is not connected at all:
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#333",
                  color: "#fff",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                No participant
              </div>
            )}
          </div>
        </div>

        {/* -------------------- */}
        {/* FOOTER CONTROLS */}
        {/* -------------------- */}
        <div className="video-call-page-footer relative flex justify-between">
          <span></span>
          <div className="flex gap-4 items-center">
            {/* Mute Toggle */}
            <div
              onClick={handleMuteToggle}
              className="flex flex-col cursor-pointer w-[150px] items-center gap-2"
            >
              <FontAwesomeIcon
                icon={isMuted ? faMicrophoneSlash : faMicrophone}
              />
              <p className="font-size-14px text-white font-Poppins-Medium">
                {isMuted ? "Unmute" : "Mute"}
              </p>
            </div>

            {/* Camera Toggle */}
            <div
              onClick={handleCameraToggle}
              className="flex flex-col cursor-pointer w-[150px] items-center gap-2"
            >
              <FontAwesomeIcon icon={isCameraOn ? faVideo : faVideoSlash} />
              <p className="font-size-14px text-white font-Poppins-Medium">
                {isCameraOn ? "Turn Off Camera" : "Turn On Camera"}
              </p>
            </div>
          </div>

          <button
            onClick={() => setConfirmModal(true)}
            className="text-white font-size-16px font-Poppins-Medium"
          >
            End
          </button>
        </div>
      </div>
    </>
  );
};

export default OnlineConsultations;
